import { motion } from 'framer-motion'
import { ArrowRightIcon, CheckIcon, ShieldCheckIcon, EyeIcon, LockClosedIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'

export default function Ciberseguridad() {
  const features = [
    {
      icon: ShieldCheckIcon,
      title: "Auditorías de Seguridad",
      description: "Evaluación completa de vulnerabilidades y riesgos de seguridad"
    },
    {
      icon: LockClosedIcon,
      title: "Protección de Datos",
      description: "Implementación de cifrado y protocolos de seguridad avanzados"
    },
    {
      icon: EyeIcon,
      title: "Monitoreo 24/7",
      description: "Vigilancia continua y detección temprana de amenazas"
    },
    {
      icon: ExclamationTriangleIcon,
      title: "Respuesta a Incidentes",
      description: "Protocolos de respuesta rápida ante brechas de seguridad"
    }
  ]

  const services = [
    "Auditorías de seguridad y pentesting",
    "Implementación de políticas de seguridad",
    "Gestión de identidades y accesos (IAM)",
    "Seguridad en aplicaciones web",
    "Compliance y certificaciones",
    "Formación en ciberseguridad"
  ]

  const threats = [
    { name: "Ransomware", severity: "Alta" },
    { name: "Phishing", severity: "Alta" },
    { name: "DDoS", severity: "Media" },
    { name: "Vulnerabilidades Web", severity: "Alta" },
    { name: "Insider Threats", severity: "Media" },
    { name: "Data Breaches", severity: "Crítica" }
  ]

  const frameworks = [
    { name: "ISO 27001", category: "Estándar" },
    { name: "NIST Framework", category: "Gobierno" },
    { name: "OWASP Top 10", category: "Web Security" },
    { name: "SOC 2", category: "Compliance" },
    { name: "GDPR", category: "Privacidad" },
    { name: "PCI DSS", category: "Pagos" }
  ]

  const stats = [
    {
      metric: "99.9%",
      label: "Detección de amenazas"
    },
    {
      metric: "<5min",
      label: "Tiempo de respuesta a incidentes"
    },
    {
      metric: "100%",
      label: "Cumplimiento regulatorio"
    },
    {
      metric: "24/7",
      label: "Monitoreo continuo"
    }
  ]

  const industries = [
    {
      name: "Banca y Finanzas",
      description: "Protección de datos financieros sensibles y cumplimiento regulatorio",
      risk: "Crítico"
    },
    {
      name: "Healthcare",
      description: "Seguridad de datos médicos y cumplimiento HIPAA",
      risk: "Alto"
    },
    {
      name: "E-commerce",
      description: "Protección de datos de clientes y transacciones seguras",
      risk: "Alto"
    },
    {
      name: "Gobierno",
      description: "Seguridad nacional y protección de información clasificada",
      risk: "Crítico"
    }
  ]

  return (
    <div 
      className="min-h-screen"
      style={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a0a2e 50%, #0a0a0a 100%)',
        color: '#ffffff'
      }}
    >
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div 
            className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl"
            style={{ background: 'rgba(239, 68, 68, 0.1)' }}
          />
          <div 
            className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl"
            style={{ background: 'rgba(59, 130, 246, 0.1)' }}
          />
        </div>

        {/* Security Animation */}
        <div className="absolute inset-0 opacity-20">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: '32px',
                height: '32px'
              }}
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.3, 1, 0.3],
                rotate: [0, 180, 360]
              }}
              transition={{
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: Math.random() * 3
              }}
            >
              <ShieldCheckIcon 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  color: '#ef4444' 
                }} 
              />
            </motion.div>
          ))}
        </div>

        <div className="relative pt-32 pb-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  style={{
                    display: 'inline-block',
                    padding: '8px 20px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.4)',
                    borderRadius: '25px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#ef4444',
                    marginBottom: '24px'
                  }}
                >
                  Ciberseguridad
                </motion.div>
                
                <motion.h1
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  style={{
                    fontSize: '48px',
                    fontWeight: '800',
                    lineHeight: '1.1',
                    color: '#ffffff',
                    marginBottom: '24px'
                  }}
                  className="lg:text-6xl"
                >
                  Protección{' '}
                  <span 
                    style={{
                      background: 'linear-gradient(135deg, #ef4444, #3b82f6)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    total
                  </span>
                </motion.h1>
                
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  style={{
                    fontSize: '20px',
                    lineHeight: '1.6',
                    color: '#d1d5db',
                    marginBottom: '32px'
                  }}
                  className="text-xl lg:text-2xl"
                >
                  Protegemos tu empresa contra amenazas cibernéticas con soluciones 
                  de seguridad avanzadas, monitoreo 24/7 y respuesta rápida a incidentes.
                </motion.p>
                
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="flex flex-col sm:flex-row gap-4"
                >
                  <Link to="/contacto">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        padding: '16px 32px',
                        background: 'linear-gradient(135deg, #ef4444, #3b82f6)',
                        color: '#ffffff',
                        fontWeight: '600',
                        borderRadius: '12px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Auditoría Gratuita
                      <ArrowRightIcon style={{ width: '20px', height: '20px' }} />
                    </motion.button>
                  </Link>
                  <Link to="/casos">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        padding: '16px 32px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        color: '#ffffff',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '16px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Ver Protecciones
                    </motion.button>
                  </Link>
                </motion.div>
              </motion.div>

              {/* Right Visual */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative flex items-center justify-center"
              >
                <div className="relative">
                  {/* Central Shield */}
                  <motion.div
                    animate={{
                      scale: [1, 1.05, 1],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{
                      duration: 8,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    style={{
                      width: '280px',
                      height: '320px',
                      background: 'linear-gradient(135deg, #ef4444, #3b82f6)',
                      clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                  >
                    <ShieldCheckIcon 
                      style={{ 
                        width: '120px', 
                        height: '120px', 
                        color: '#ffffff' 
                      }} 
                    />
                  </motion.div>

                  {/* Orbiting Security Elements */}
                  {[
                    { icon: LockClosedIcon, rotation: 0, radius: 180 },
                    { icon: EyeIcon, rotation: 72, radius: 180 },
                    { icon: ExclamationTriangleIcon, rotation: 144, radius: 180 },
                    { icon: ShieldCheckIcon, rotation: 216, radius: 180 },
                    { icon: LockClosedIcon, rotation: 288, radius: 180 }
                  ].map((element, index) => (
                    <motion.div
                      key={index}
                      className="absolute"
                      style={{
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        transformOrigin: `0 0`
                      }}
                      animate={{
                        rotate: element.rotation + 360
                      }}
                      transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                    >
                      <motion.div
                        style={{
                          width: '60px',
                          height: '60px',
                          background: 'rgba(239, 68, 68, 0.8)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transform: `translateY(-${element.radius}px)`
                        }}
                        animate={{
                          rotate: -(element.rotation + 360)
                        }}
                        transition={{
                          duration: 20,
                          repeat: Infinity,
                          ease: "linear"
                        }}
                      >
                        <element.icon 
                          style={{ 
                            width: '30px', 
                            height: '30px', 
                            color: '#ffffff' 
                          }} 
                        />
                      </motion.div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '16px'
              }}
              className="text-4xl lg:text-5xl"
            >
              Servicios de Seguridad
            </h2>
            <p 
              style={{
                fontSize: '18px',
                color: '#d1d5db',
                maxWidth: '800px',
                margin: '0 auto'
              }}
              className="text-xl"
            >
              Protección integral contra amenazas cibernéticas con tecnologías 
              de seguridad de última generación y expertise especializado
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                    padding: '32px',
                    textAlign: 'center',
                    transition: 'all 0.3s ease'
                  }}
                  className="group hover:border-red-500/30"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    style={{
                      width: '64px',
                      height: '64px',
                      background: 'linear-gradient(135deg, #ef4444, #3b82f6)',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px auto'
                    }}
                  >
                    <IconComponent 
                      style={{ 
                        width: '32px', 
                        height: '32px', 
                        color: '#ffffff' 
                      }} 
                    />
                  </motion.div>
                  <h3 
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#ffffff',
                      marginBottom: '12px'
                    }}
                  >
                    {feature.title}
                  </h3>
                  <p 
                    style={{
                      fontSize: '16px',
                      color: '#d1d5db',
                      lineHeight: '1.6'
                    }}
                  >
                    {feature.description}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section 
        className="py-20 px-6"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 
                style={{
                  fontSize: '36px',
                  fontWeight: '700',
                  color: '#ffffff',
                  marginBottom: '24px'
                }}
                className="text-4xl lg:text-5xl"
              >
                Servicios Especializados
              </h2>
              <p 
                style={{
                  fontSize: '18px',
                  color: '#d1d5db',
                  marginBottom: '32px'
                }}
                className="text-xl"
              >
                Cobertura completa en ciberseguridad, desde evaluaciones 
                hasta implementación y monitoreo continuo
              </p>
              
              <div className="space-y-4">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <CheckIcon 
                      style={{ 
                        width: '24px', 
                        height: '24px', 
                        color: '#ef4444',
                        flexShrink: 0
                      }} 
                    />
                    <span 
                      style={{
                        fontSize: '16px',
                        color: '#ffffff',
                        fontWeight: '500'
                      }}
                    >
                      {service}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Threats Matrix */}
              <div 
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '24px',
                  padding: '48px'
                }}
              >
                <h3 
                  style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#ffffff',
                    marginBottom: '24px',
                    textAlign: 'center'
                  }}
                >
                  Amenazas que Neutralizamos
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {threats.map((threat, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.02 }}
                      style={{
                        padding: '16px',
                        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(59, 130, 246, 0.1))',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <span 
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#ffffff'
                        }}
                      >
                        {threat.name}
                      </span>
                      <span 
                        style={{
                          fontSize: '12px',
                          color: threat.severity === 'Crítica' ? '#ef4444' : 
                                 threat.severity === 'Alta' ? '#f59e0b' : '#10b981',
                          fontWeight: '600',
                          padding: '4px 12px',
                          background: threat.severity === 'Crítica' ? 'rgba(239, 68, 68, 0.2)' : 
                                     threat.severity === 'Alta' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                          borderRadius: '8px'
                        }}
                      >
                        {threat.severity}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '16px'
              }}
              className="text-4xl lg:text-5xl"
            >
              Resultados de Seguridad
            </h2>
            <p 
              style={{
                fontSize: '18px',
                color: '#d1d5db',
                maxWidth: '800px',
                margin: '0 auto'
              }}
              className="text-xl"
            >
              Nuestras implementaciones de seguridad entregan protección 
              verificable y medible para tu empresa
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                style={{
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(59, 130, 246, 0.1))',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '16px',
                  padding: '32px',
                  textAlign: 'center'
                }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    fontSize: '48px',
                    fontWeight: '800',
                    color: '#ef4444',
                    marginBottom: '12px'
                  }}
                  className="text-5xl lg:text-6xl"
                >
                  {stat.metric}
                </motion.div>
                <p 
                  style={{
                    fontSize: '16px',
                    color: '#ffffff',
                    fontWeight: '500',
                    lineHeight: '1.5'
                  }}
                >
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Frameworks Section */}
      <section 
        className="py-20 px-6"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '16px'
              }}
              className="text-4xl lg:text-5xl"
            >
              Frameworks y Compliance
            </h2>
            <p 
              style={{
                fontSize: '18px',
                color: '#d1d5db',
                maxWidth: '800px',
                margin: '0 auto'
              }}
              className="text-xl"
            >
              Implementamos los estándares de seguridad más reconocidos 
              y aseguramos el cumplimiento regulatorio
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {frameworks.map((framework, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '16px',
                  padding: '24px 16px',
                  textAlign: 'center',
                  transition: 'all 0.3s ease'
                }}
              >
                <h4 
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#ffffff',
                    marginBottom: '8px'
                  }}
                >
                  {framework.name}
                </h4>
                <span 
                  style={{
                    fontSize: '12px',
                    color: '#ef4444',
                    fontWeight: '500',
                    padding: '4px 8px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: '6px'
                  }}
                >
                  {framework.category}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '16px'
              }}
              className="text-4xl lg:text-5xl"
            >
              Industrias de Alto Riesgo
            </h2>
            <p 
              style={{
                fontSize: '18px',
                color: '#d1d5db',
                maxWidth: '800px',
                margin: '0 auto'
              }}
              className="text-xl"
            >
              Especialización en sectores con requisitos críticos de seguridad
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '16px',
                  padding: '32px',
                  transition: 'all 0.3s ease'
                }}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 
                    style={{
                      fontSize: '24px',
                      fontWeight: '600',
                      color: '#ef4444',
                      marginBottom: '16px'
                    }}
                  >
                    {industry.name}
                  </h3>
                  <span 
                    style={{
                      fontSize: '12px',
                      color: industry.risk === 'Crítico' ? '#ef4444' : '#f59e0b',
                      fontWeight: '600',
                      padding: '4px 12px',
                      background: industry.risk === 'Crítico' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                      borderRadius: '8px'
                    }}
                  >
                    {industry.risk}
                  </span>
                </div>
                <p 
                  style={{
                    fontSize: '16px',
                    color: '#d1d5db',
                    lineHeight: '1.6'
                  }}
                >
                  {industry.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            style={{
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(59, 130, 246, 0.2))',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '24px',
              padding: '48px'
            }}
          >
            <h2 
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '16px'
              }}
              className="text-4xl lg:text-5xl"
            >
              ¿Estás protegido?
            </h2>
            <p 
              style={{
                fontSize: '20px',
                color: '#d1d5db',
                marginBottom: '32px',
                maxWidth: '600px',
                margin: '0 auto 32px auto'
              }}
            >
              No esperes a ser víctima de un ciberataque. Protege tu empresa ahora con nuestras soluciones de seguridad
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contacto">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '16px 32px',
                    background: 'linear-gradient(135deg, #ef4444, #3b82f6)',
                    color: '#ffffff',
                    fontWeight: '600',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Evaluación de Seguridad
                  <ArrowRightIcon style={{ width: '20px', height: '20px' }} />
                </motion.button>
              </Link>
              <Link to="/casos">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '16px 32px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '16px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Ver Casos de Seguridad
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
